import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Send, Trash2, UserPlus, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const RUOLI = [["cliente", "Cliente"], ["notaio", "Notaio"], ["agenzia", "Agenzia"], ["amministratore", "Amministratore"], ["proprietario", "Proprietario"], ["tecnico", "Tecnico"]];

export const SendReportCard = ({ pratica }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ nome: "", email: "", ruolo: "cliente" });
  const [msg, setMsg] = useState("");
  const [sel, setSel] = useState(null);
  const dest = pratica.destinatari || [];
  const invii = pratica.invii || [];
  const inv = () => qc.invalidateQueries({ queryKey: ["pratica", pratica.id] });

  const addMut = useMutation({
    mutationFn: async () => (await api.post(`/pratiche/${pratica.id}/destinatari`, form)).data,
    onSuccess: () => { inv(); setForm({ nome: "", email: "", ruolo: "cliente" }); toast.success("Destinatario aggiunto"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/pratiche/${pratica.id}/destinatari/${id}`)).data,
    onSuccess: inv,
  });
  const sendMut = useMutation({
    mutationFn: async () => (await api.post(`/pratiche/${pratica.id}/send-report`, { destinatario_id: sel, messaggio: msg })).data,
    onSuccess: (r) => { inv(); setMsg(""); toast.success(`Report inviato a ${r.email}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Invio non riuscito"),
  });

  return (
    <div data-testid="send-report-card" className="rounded-xl border border-slate-800 bg-[#121824] p-5">
      <h3 className="font-heading font-semibold flex items-center gap-2 mb-1"><Mail className="h-4 w-4 text-cyan-400" /> Invia Report al Cliente / Notaio</h3>
      <p className="text-xs text-slate-500 mb-4">Il PDF viene allegato all'email insieme a un link di consultazione valido 7 giorni.</p>

      <div className="grid sm:grid-cols-[1fr_1.3fr_auto_auto] gap-2 mb-3">
        <input data-testid="dest-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome"
          className="px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm" />
        <input data-testid="dest-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@esempio.it"
          className="px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm" />
        <select data-testid="dest-ruolo-select" value={form.ruolo} onChange={(e) => setForm({ ...form, ruolo: e.target.value })}
          className="px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm">
          {RUOLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button data-testid="dest-add-btn" disabled={!form.email || addMut.isPending} onClick={() => addMut.mutate()}
          className="px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-sm inline-flex items-center gap-1.5 hover:border-cyan-500/50 disabled:opacity-40"><UserPlus className="h-4 w-4" /> Aggiungi</button>
      </div>

      {dest.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {dest.map((d) => {
            const sent = invii.filter((i) => i.destinatario_id === d.id);
            return (
              <label key={d.id} data-testid={`dest-row-${d.id}`} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${sel === d.id ? "border-cyan-500/60 bg-cyan-500/[0.04]" : "border-slate-800 bg-[#0B0F17]"}`}>
                <input type="radio" data-testid={`dest-select-${d.id}`} checked={sel === d.id} onChange={() => setSel(d.id)} className="accent-cyan-400" />
                <div className="flex-1 min-w-0 text-sm">
                  <p className="truncate">{d.nome} <span className="text-slate-500 text-xs">· {d.ruolo}</span></p>
                  <p className="text-xs text-slate-500 truncate">{d.email}</p>
                </div>
                {sent.length > 0 && <span data-testid={`dest-sent-${d.id}`} className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> {sent.length} inviat{sent.length === 1 ? "o" : "i"}</span>}
                <button data-testid={`dest-delete-${d.id}`} onClick={(e) => { e.preventDefault(); delMut.mutate(d.id); }} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </label>
            );
          })}
        </div>
      ) : <p className="text-xs text-slate-600 mb-3">Nessun destinatario: aggiungi il cliente o il notaio.</p>}

      <textarea data-testid="send-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Messaggio personale (opzionale)"
        className="w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm mb-3" />
      <button data-testid="send-report-btn" disabled={!sel || !pratica.analysis || sendMut.isPending} onClick={() => sendMut.mutate()}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-500 text-slate-900 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-40">
        <Send className="h-4 w-4" /> {sendMut.isPending ? "Invio in corso…" : "Invia report PDF"}
      </button>
      {!pratica.analysis && <p className="text-[11px] text-slate-600 mt-2">Esegui prima l'analisi per poter inviare il report.</p>}
    </div>
  );
};
